<template>
  <div class="desktop-toolbar-sidebar" :class="{ active: hasSelection }">
    <!-- Undo/Redo buttons always visible at top -->
    <div class="undo-redo-section">
      <button 
        class="undo-redo-btn" 
        :class="{ disabled: !canUndo }"
        :disabled="!canUndo"
        @click="$emit('undo')"
        :title="$t('legacy.Отменить (Ctrl+Z)')"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 10h10a5 5 0 0 1 5 5v0a5 5 0 0 1-5 5H7"/>
          <path d="M7 6L3 10l4 4"/>
        </svg>
      </button>
      <button 
        class="undo-redo-btn" 
        :class="{ disabled: !canRedo }"
        :disabled="!canRedo"
        @click="$emit('redo')"
        :title="$t('legacy.Повторить (Ctrl+Y)')"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10H11a5 5 0 0 0-5 5v0a5 5 0 0 0 5 5h6"/>
          <path d="M17 6l4 4-4 4"/>
        </svg>
      </button>
    </div>

    <!-- Price info section -->
    <div v-if="canSeePrices" class="price-info-section">
      <div class="price-row zone-price">
        <span class="price-label">{{ $t('legacy.Зона') }}:</span>
        <span class="price-value">{{ zoneTotalPrice }} ₽</span>
      </div>
      <div class="price-row project-price">
        <span class="price-label">{{ $t('estimate.project') }}:</span>
        <span class="price-value">{{ projectTotalPrice }} ₽</span>
      </div>
    </div>

    <!-- Calculator button (only for premium) -->
    <button 
      v-if="canUseCalculator" 
      class="calculator-btn" 
      :class="{ active: calculatorPanelOpen }"
      @click="$emit('open-calculator')"
      :title="$t('legacy.Калькулятор')"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="4" y="2" width="16" height="20" rx="2"/>
        <line x1="8" y1="6" x2="16" y2="6"/>
        <circle cx="8" cy="11" r="1" fill="currentColor" stroke="none"/>
        <circle cx="12" cy="11" r="1" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="11" r="1" fill="currentColor" stroke="none"/>
        <circle cx="8" cy="15" r="1" fill="currentColor" stroke="none"/>
        <circle cx="12" cy="15" r="1" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="15" r="1" fill="currentColor" stroke="none"/>
      </svg>
      <span>{{ $t('legacy.Калькулятор') }}</span>
    </button>

    <div v-if="!hasSelection" class="sidebar-actions-menu">
      <div class="actions-list">
        <button class="action-item" @click="$emit('toggle-selection-mode')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <span>{{ $t('legacy.Группировка') }}</span>
        </button>
        
        <button class="action-item" @click="$emit('open-background')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
          <span>{{ $t('legacy.Фон') }}</span>
        </button>
        
        <button class="action-item" @click="$emit('open-layers')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polygon points="12 2 2 7 12 12 22 7 12 2"/>
            <polyline points="2 17 12 22 22 17"/>
            <polyline points="2 12 12 17 22 12"/>
          </svg>
          <span>{{ $t('legacy.Слои') }}</span>
        </button>
        
        <div class="action-divider"></div>
        
        <button class="action-item" @click="$emit('save')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          <span>{{ $t('legacy.Сохранить') }}</span>
        </button>
        
        <button class="action-item action-download" @click="$emit('download')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>{{ $t('legacy.Скачать') }}</span>
        </button>
        
        <button class="action-item" :class="{ 'action-locked': !canUseAi }" @click="$emit('ai-process')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          <span>{{ $t('editor.aiProcessing') }}</span>
          <svg v-if="!canUseAi" class="lock-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </button>
        
        <div class="action-divider"></div>
        
        <button class="action-item" @click="$emit('export-pdf')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          <span>{{ $t('legacy.Смета PDF') }}</span>
        </button>
        
        <button class="action-item" @click="$emit('export-excel')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          <span>{{ $t('legacy.Смета Excel') }}</span>
        </button>
        
        <div class="action-divider"></div>
        
        <button class="action-item action-danger" @click="$emit('clear-all')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
          </svg>
          <span>{{ $t('legacy.Очистить всё') }}</span>
        </button>
        
        <button class="action-item action-danger" @click="$emit('delete-zone')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          <span>{{ $t('legacy.Удалить зону') }}</span>
        </button>
      </div>
    </div>

    <div v-else class="sidebar-content">
      <div class="sidebar-header">
        <span class="selection-type">{{ selectionTypeLabel }}</span>
      </div>

      <div class="toolbar-sections">
        <div class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Основные') }}</div>
          <div class="toolbar-buttons">
            <button 
              class="toolbar-btn" 
              :class="{ active: isLocked }" 
              @click="$emit('toggle-lock')"
              :title="isLocked ? $t('legacy.Разблокировать') : $t('legacy.Заблокировать')"
            >
              <img :src="keyIcon" alt="lock" />
              <span>{{ isLocked ? $t('legacy.Разблокировать') : $t('legacy.Блокировка') }}</span>
            </button>

            <button class="toolbar-btn" @click="$emit('copy')" :title="$t('legacy.Копировать')">
              <img :src="copyIcon" alt="copy" />
              <span>{{ $t('legacy.Копировать') }}</span>
            </button>

            <button 
              v-if="selectionType !== 'text'" 
              class="toolbar-btn" 
              @click="$emit('mirror')" 
              :title="$t('legacy.Отразить')"
            >
              <img :src="mirrorIcon" alt="mirror" />
              <span>{{ $t('legacy.Отразить') }}</span>
            </button>

            <button class="toolbar-btn delete-btn" @click="$emit('delete')" :title="$t('legacy.Удалить')">
              <img :src="deleteIcon" alt="delete" />
              <span>{{ $t('legacy.Удалить') }}</span>
            </button>
          </div>
        </div>

        <div v-if="$slots['transform-tools']" class="toolbar-section">
          <div class="section-title">{{ $t('editor.transformTools') }}</div>
          <slot name="transform-tools" />
        </div>
        <div v-if="$slots['eraser-tools']" class="toolbar-section">
          <slot name="eraser-tools" />
        </div>
        <div class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Поворот') }}</div>
          <div class="slider-control">
            <input
              type="range"
              class="opacity-slider"
              :value="rotation"
              @input="handleRotationChange"
              min="0"
              max="360"
              step="1"
              :style="rotationSliderStyle"
            />
            <div class="value-input-wrap">
              <input
                type="number"
                class="value-input"
                :value="rotation"
                @input="handleRotationNumberInput"
                @blur="handleRotationNumberBlur"
                min="0"
                max="360"
                step="1"
              />
              <span class="value-suffix">°</span>
            </div>
          </div>
        </div>

        <div class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Прозрачность') }}</div>
          <div class="slider-control">
            <input
              type="range"
              class="opacity-slider"
              :value="Math.round(opacity * 100)"
              @input="handleOpacityChange"
              min="0"
              max="100"
              step="1"
              :style="sliderStyle"
            />
            <div class="value-input-wrap">
              <input
                type="number"
                class="value-input"
                :value="Math.round(opacity * 100)"
                @input="handleOpacityNumberInput"
                @blur="handleOpacityNumberBlur"
                min="0"
                max="100"
                step="1"
              />
              <span class="value-suffix">%</span>
            </div>
          </div>
        </div>

        <div class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Слои') }}</div>
          <div class="layers-buttons">
            <button class="layers-btn" @click="$emit('move-layer', 'front')" :title="$t('legacy.На передний план')">
              <img :src="upFullIcon" alt="front" />
              <span>{{ $t('legacy.Наверх') }}</span>
            </button>
            <button class="layers-btn" @click="$emit('move-layer', 'up')" :title="$t('editor.moveUp')">
              <img :src="upIcon" alt="up" />
              <span>{{ $t('legacy.Выше') }}</span>
            </button>
            <button class="layers-btn" @click="$emit('move-layer', 'down')" :title="$t('editor.moveDown')">
              <img :src="downIcon" alt="down" />
              <span>{{ $t('legacy.Ниже') }}</span>
            </button>
            <button class="layers-btn" @click="$emit('move-layer', 'back')" :title="$t('legacy.На задний план')">
              <img :src="downFullIcon" alt="back" />
              <span>{{ $t('editor.moveDown') }}</span>
            </button>
          </div>
        </div>

        <div v-if="selectionType !== 'text' && selectionType !== 'group'" class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Фильтры') }}</div>
          <div class="filters-list">
            <div v-for="filter in filtersList" :key="filter.name" class="filter-control">
              <span class="filter-label">{{ getFilterLabel(filter.name) }}</span>
              <div class="filter-slider-wrap">
                <input
                  type="range"
                  class="filter-slider"
                  :value="getFilterValue(filter.name)"
                  @input="(e) => handleFilterChange(e, filter.name)"
                  :min="filter.min"
                  :max="filter.max"
                  :step="filter.step"
                  :style="getFilterSliderStyle(filter)"
                />
                <span class="filter-value">{{ getFilterDisplayValue(filter.name) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectionType === 'text'" class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Размер шрифта') }}</div>
          <div class="slider-control">
            <input
              type="range"
              class="opacity-slider"
              :value="fontSize"
              @input="handleFontSizeChange"
              min="12"
              max="360"
              step="1"
              :style="fontSizeSliderStyle"
            />
            <div class="value-input-wrap">
              <input
                type="number"
                class="value-input"
                :value="fontSize"
                @input="handleFontSizeNumberInput"
                @blur="handleFontSizeNumberBlur"
                min="12"
                max="360"
                step="1"
              />
              <span class="value-suffix">px</span>
            </div>
          </div>
        </div>

        <div v-if="selectionType === 'text'" class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Цвет текста') }}</div>
          <div class="text-controls">
            <div class="color-control">
              <input 
                type="color" 
                class="color-input" 
                :value="textColor" 
                @input="handleColorChange"
              />
            </div>
            <div v-if="projectColors.length > 0" class="project-colors">
              <div 
                v-for="color in projectColors" 
                :key="color" 
                class="project-color-swatch"
                :style="{ backgroundColor: color }"
                :class="{ active: color === textColor }"
                @click="handleQuickColorSelect(color)"
                :title="color"
              ></div>
            </div>
            <button 
              class="toolbar-btn" 
              :class="{ active: textShadow }" 
              @click="$emit('toggle-shadow')"
              :title="$t('Тень')"
            >
              <img :src="shadowIcon" alt="shadow" />
              <span>{{ $t('Тень') }}</span>
            </button>
          </div>
        </div>

        <div v-if="selectionType === 'group'" class="toolbar-section">
          <div class="section-title">{{ $t('legacy.Группа') }}</div>
          <button class="toolbar-btn ungroup-btn" @click="$emit('ungroup')" :title="$t('legacy.Разгруппировать')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="3" y="3" width="8" height="8" rx="1"/>
              <rect x="13" y="13" width="8" height="8" rx="1"/>
              <path d="M11 7L13 7M17 11L17 13M7 11L7 13M11 17L13 17" stroke-linecap="round"/>
            </svg>
            <span>{{ $t('legacy.Разгруппировать') }}</span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { 
  keyIcon, 
  copyIcon, 
  mirrorIcon, 
  deleteElement, 
  shadowIcon,
  up,
  upFull,
  down,
  downFull
} from '@/assets/images/content';

export default {
  name: 'DesktopToolbarSidebar',
  props: {
    selectionType: {
      type: String,
      default: null
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    opacity: {
      type: Number,
      default: 1
    },
    rotation: {
      type: Number,
      default: 0
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    textColor: {
      type: String,
      default: '#000000'
    },
    textShadow: {
      type: Boolean,
      default: false
    },
    filtersList: {
      type: Array,
      default: () => []
    },
    fonts: {
      type: Array,
      default: () => []
    },
    currentFont: {
      type: String,
      default: 'Arial'
    },
    fontSize: {
      type: Number,
      default: 32
    },
    projectColors: {
      type: Array,
      default: () => []
    },
    canUndo: {
      type: Boolean,
      default: false
    },
    canRedo: {
      type: Boolean,
      default: false
    },
    canUseAi: {
      type: Boolean,
      default: false
    },
    zoneTotalPrice: {
      type: Number,
      default: 0
    },
    projectTotalPrice: {
      type: Number,
      default: 0
    },
    canSeePrices: {
      type: Boolean,
      default: false
    },
    canUseCalculator: {
      type: Boolean,
      default: false
    },
    calculatorPanelOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'toggle-lock',
    'copy',
    'mirror',
    'undo',
    'redo',
    'update-rotation',
    'update-opacity',
    'move-layer',
    'update-filter',
    'update-color',
    'update-font',
    'update-font-size',
    'toggle-shadow',
    'ungroup',
    'delete',
    'toggle-selection-mode',
    'open-background',
    'open-layers',
    'save',
    'download',
    'ai-process',
    'export-pdf',
    'export-excel',
    'clear-all',
    'delete-zone',
    'open-calculator'
  ],
  data() {
    return {
      keyIcon,
      copyIcon,
      mirrorIcon,
      deleteIcon: deleteElement,
      shadowIcon,
      upIcon: up,
      upFullIcon: upFull,
      downIcon: down,
      downFullIcon: downFull
    };
  },
  computed: {
    hasSelection() {
      return this.selectionType !== null;
    },
    selectionTypeLabel() {
      const labels = {
        image: this.$t('legacy.Объект'),
        text: this.$t('legacy.Текст'),
        group: this.$t('legacy.Группа')
      };
      return labels[this.selectionType] || this.$t('editor.element');
    },
    sliderStyle() {
      const percent = this.opacity * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 0%, #4A90D9 ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`
      };
    },
    rotationSliderStyle() {
      const percent = (this.rotation / 360) * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 0%, #4A90D9 ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`
      };
    },
    fontSizeSliderStyle() {
      const percent = ((this.fontSize - 12) / (120 - 12)) * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 0%, #4A90D9 ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`
      };
    }
  },
  methods: {
    handleOpacityChange(e) {
      const value = parseInt(e.target.value) / 100;
      this.$emit('update-opacity', value);
    },
    handleOpacityNumberInput(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) return;
      value = Math.max(0, Math.min(100, value));
      this.$emit('update-opacity', value / 100);
    },
    handleOpacityNumberBlur(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) value = 100;
      value = Math.max(0, Math.min(100, value));
      this.$emit('update-opacity', value / 100);
    },
    handleRotationChange(e) {
      const value = parseInt(e.target.value);
      this.$emit('update-rotation', value);
    },
    handleRotationNumberInput(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) return;
      value = Math.max(0, Math.min(360, value));
      this.$emit('update-rotation', value);
    },
    handleRotationNumberBlur(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) value = 0;
      value = Math.max(0, Math.min(360, value));
      this.$emit('update-rotation', value);
    },
    handleFilterChange(e, filterName) {
      const value = parseFloat(e.target.value);
      this.$emit('update-filter', filterName, value);
    },
    getFilterValue(filterName) {
      return this.filters?.[filterName] ?? 0;
    },
    getFilterDisplayValue(filterName) {
      const value = this.filters?.[filterName] ?? 0;
      return Number.isInteger(value) ? value : value.toFixed(1);
    },
    getFilterLabel(name) {
      const labels = {
        exposure: this.$t('legacy.Экспозиция'),
        contrast: this.$t('legacy.Контраст'),
        saturation: this.$t('legacy.Насыщенность'),
        temperature: this.$t('legacy.Температура'),
        hue: this.$t('legacy.Оттенок'),
        highlights: this.$t('legacy.Света'),
        shadows: this.$t('legacy.Тени')
      };
      return labels[name] || name;
    },
    getFilterSliderStyle(filter) {
      const value = this.getFilterValue(filter.name);
      const range = filter.max - filter.min;
      const percent = ((value - filter.min) / range) * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 0%, #4A90D9 ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`
      };
    },
    handleColorChange(e) {
      this.$emit('update-color', e.target.value);
    },
    handleFontChange(e) {
      this.$emit('update-font', e.target.value);
    },
    handleFontSizeChange(e) {
      const value = parseInt(e.target.value);
      this.$emit('update-font-size', value);
    },
    handleFontSizeNumberInput(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) return;
      value = Math.max(12, Math.min(360, value));
      this.$emit('update-font-size', value);
    },
    handleFontSizeNumberBlur(e) {
      let value = parseInt(e.target.value);
      if (isNaN(value)) value = 32;
      value = Math.max(12, Math.min(360, value));
      this.$emit('update-font-size', value);
    },
    handleQuickColorSelect(color) {
      this.$emit('update-color', color);
    }
  }
};
</script>

<style lang="scss" scoped>
.desktop-toolbar-sidebar {
  position: fixed;
  top: 120px;
  right: 20px;
  height: calc(96vh - 120px);
  width: 180px;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden;
  transition: opacity 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 40;
}

.undo-redo-section {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.undo-redo-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    stroke: #374151;
  }

  &:hover:not(.disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  &:active:not(.disabled) {
    background: #e5e7eb;
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;

    svg {
      stroke: #9ca3af;
    }
  }
}

.price-info-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #e0f4fc;
  border-bottom: 1px solid #c4dff0;
  flex-shrink: 0;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
  }

  .price-label {
    color: #6b7280;
  }

  .price-value {
    font-weight: 600;
    color: #496782;
  }

  .zone-price .price-value {
    color: #26374E;
  }
}

.calculator-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  background: #f8fafc;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  transition: background 0.2s;

  &:hover {
    background: #e0f4fc;
    color: #496782;
  }

  &.active {
    background: #e0f4fc;
    color: #496782;
  }

  svg {
    flex-shrink: 0;
  }
}

.sidebar-actions-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 12px;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  svg {
    flex-shrink: 0;
    stroke: #6b7280;
  }

  span {
    font-size: 13px;
    color: #374151;
    flex: 1;
  }

  .lock-icon {
    stroke: #9ca3af;
  }

  &:hover {
    background: #f3f4f6;
  }

  &.action-download {
    svg {
      stroke: #3b82f6;
    }
    span {
      color: #3b82f6;
    }
  }

  &.action-locked {
    opacity: 0.7;
    
    &:hover {
      background: #f9fafb;
    }
  }

  &.action-danger {
    svg {
      stroke: #dc2626;
    }
    span {
      color: #dc2626;
    }

    &:hover {
      background: #fef2f2;
    }
  }
}

.action-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 8px 0;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;

  .selection-type {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
}

.toolbar-sections {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
}

.toolbar-section {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .section-title {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
  }
}

.toolbar-buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.toolbar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  flex: 1;
  min-width: 60px;

  img {
    width: 20px;
    height: 20px;
    opacity: 0.7;
  }

  span {
    font-size: 8px;
    color: #6b7280;
    white-space: nowrap;
  }

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  &.active {
    background: #e0f2fe;
    border-color: #7dd3fc;

    img {
      opacity: 1;
    }

    span {
      color: #0284c7;
    }
  }
}

.slider-control {
  display: flex;
  align-items: center;
  gap: 8px;

  .opacity-slider {
    width: 90px;
    flex-shrink: 0;
    height: 6px;
    border-radius: 3px;
    appearance: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #4A90D9;
      border: 2px solid #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      cursor: pointer;
    }
  }

  .slider-value {
    font-size: 12px;
    color: #6b7280;
    min-width: 32px;
    text-align: right;
    flex-shrink: 0;
  }
  
  .value-input-wrap {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    
    .value-input {
      width: 40px;
      padding: 4px 6px;
      font-size: 12px;
      text-align: center;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      background: #fff;
      color: #374151;
      -moz-appearance: textfield;
      
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      &:focus {
        outline: none;
        border-color: #4A90D9;
      }
    }
    
    .value-suffix {
      font-size: 12px;
      color: #6b7280;
    }
  }
}

.layers-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}

.layers-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  img {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }

  span {
    font-size: 11px;
    color: #6b7280;
  }

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
}

.filters-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-control {
  .filter-label {
    display: block;
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .filter-slider-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .filter-slider {
    width: 85px;
    flex-shrink: 0;
    height: 4px;
    border-radius: 2px;
    appearance: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #4A90D9;
      border: 2px solid #fff;
      box-shadow: 0 1px 2px rgba(0,0,0,0.15);
      cursor: pointer;
    }
  }

  .filter-value {
    font-size: 11px;
    color: #9ca3af;
    min-width: 24px;
    text-align: right;
    flex-shrink: 0;
  }
}

.text-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.color-control {
  display: flex;
  align-items: center;
  gap: 10px;

  .control-label {
    font-size: 12px;
    color: #6b7280;
  }

  .color-input {
    width: 32px;
    height: 32px;
    padding: 0;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    background: transparent;

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    &::-webkit-color-swatch {
      border: none;
      border-radius: 4px;
    }
    &::-moz-color-swatch {
      border: none;
      border-radius: 4px;
    }
  }
}

.font-selector {
  .font-select {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    background: #fff;
    cursor: pointer;
    
    &:focus {
      outline: none;
      border-color: #4A90D9;
    }
  }
}

.project-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
  
  .project-color-swatch {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 2px solid #e5e7eb;
    cursor: pointer;
    transition: transform 0.15s ease, border-color 0.15s ease;
    
    &:hover {
      transform: scale(1.1);
    }
    
    &.active {
      border-color: #4A90D9;
      box-shadow: 0 0 0 2px rgba(74, 144, 217, 0.3);
    }
  }
}

.font-preview {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.ungroup-btn {
  width: 100%;
  flex-direction: row;
  justify-content: center;
  gap: 8px;

  svg {
    stroke: #6b7280;
  }
}

.delete-btn {
  background: #fef2f2;
  border-color: #fecaca;

  img {
    opacity: 1;
  }

  span {
    color: #dc2626;
  }

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
  }
}

/* Tablet styles - make sidebar scrollable and compact */
@media (max-width: 1200px) and (min-width: 769px) {
  .desktop-toolbar-sidebar {
    top: 70px;
    height: calc(100vh - 90px);
    width: 160px;
    min-width: 160px;
    overflow-y: auto;
  }

  .sidebar-header {
    padding: 10px 12px;
  }

  .toolbar-sections {
    padding: 8px;
  }

  .toolbar-section {
    margin-bottom: 10px;
    padding-bottom: 10px;

    .section-title {
      font-size: 10px;
      margin-bottom: 6px;
    }
  }

  .toolbar-buttons {
    gap: 4px;
  }

  .toolbar-btn {
    padding: 6px;
    min-height: 32px;

    img {
      width: 16px;
      height: 16px;
    }

    span {
      font-size: 9px;
    }
  }

  .filter-slider {
    .filter-label {
      font-size: 10px;
    }
  }

}
</style>
